import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, ArrowLeft } from "lucide-react";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    }, 2000);
  };

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFEBB5] to-[#FFF8E6]">
      {/* Header */}
      <div className="bg-[#67142D] text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-source-serif-4 font-bold">Contact Us</h1>
              <p className="text-lg mt-2 font-inter">
                Get in touch with Vishwa Sanatan Mahapeeth
              </p>
            </div>
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-source-serif-4 font-bold text-[#67142D] mb-6">
                Get in Touch
              </h2>
              <p className="text-[#67142D]/80 text-lg font-inter leading-relaxed">
                We would love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="bg-[#67142D] p-3 rounded-lg">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#67142D] text-lg mb-2">Address</h3>
                  <p className="text-[#67142D]/80 font-inter">
                    तीर्थ आश्रम, शिवलोक कॉलोनी, भगत सिंह चौराहे के पास<br />
                    हरिद्वार, उत्तराखंड - 249401
                  </p>
                </div>
              </div>

              {/* Phone Numbers */}
              <div className="flex items-start gap-4">
                <div className="bg-[#67142D] p-3 rounded-lg">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#67142D] text-lg mb-2">Phone Numbers</h3>
                  <div className="space-y-1">
                    <p className="text-[#67142D]/80 font-inter">
                      <a href="tel:+919520050068" className="hover:text-[#B51F3B] transition-colors">
                        +91 9520050068
                      </a>
                    </p>
                    <p className="text-[#67142D]/80 font-inter">
                      <a href="tel:+919520050038" className="hover:text-[#B51F3B] transition-colors">
                        +91 9520050038
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="flex items-start gap-4">
                <div className="bg-[#67142D] p-3 rounded-lg">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#67142D] text-lg mb-2">Follow Us</h3>
                  <div className="space-y-2">
                    <p className="text-[#67142D]/80 font-inter">
                      <a 
                        href="https://www.facebook.com/share/1AVFg6W3SA/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-[#B51F3B] transition-colors"
                      >
                        Facebook
                      </a>
                    </p>
                    <p className="text-[#67142D]/80 font-inter">
                      <a 
                        href="https://www.instagram.com/vsmahapeeth" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-[#B51F3B] transition-colors"
                      >
                        Instagram
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-6">
              Send us a Message
            </h3>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-800 font-inter">
                  Thank you for your message! We'll get back to you soon.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name and Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#67142D] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#67142D] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              {/* Phone and Subject */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#67142D] mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent"
                    placeholder="Enter your phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#67142D] mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent"
                    placeholder="Enter subject"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-[#67142D] mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#67142D] focus:border-transparent"
                  placeholder="Enter your message here..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#67142D] text-white py-3 px-6 rounded-lg hover:bg-[#B51F3B] transition-colors duration-300 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-16 bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-2xl font-source-serif-4 font-bold text-[#67142D] mb-6 text-center">
            About Vishwa Sanatan Mahapeeth
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="bg-[#67142D] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h4 className="font-semibold text-[#67142D] mb-2">Spiritual Center</h4>
              <p className="text-[#67142D]/80 text-sm font-inter">
                A divine center for spiritual awakening and Vedic knowledge
              </p>
            </div>
            <div>
              <div className="bg-[#67142D] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h4 className="font-semibold text-[#67142D] mb-2">Located in Haridwar</h4>
              <p className="text-[#67142D]/80 text-sm font-inter">
                Situated in the holy city of Haridwar, Uttarakhand
              </p>
            </div>
            <div>
              <div className="bg-[#67142D] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h4 className="font-semibold text-[#67142D] mb-2">Always Available</h4>
              <p className="text-[#67142D]/80 text-sm font-inter">
                Reach out to us anytime for spiritual guidance
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
